import React from "react";
import { Card, CardHeader, CardTitle, CardDescription } from "components/ui/card";
import { cn } from "lib/utils";

interface AlertFilter {
  discrepancy_type?: string;
  untracked?: string;
  arrival?: string;
  cust_id?: string;
  pod_not_received?: string;
  pending_invoice?: string;
}

interface AlertBucket {
  label: string;
  count: number;
  filters: AlertFilter;
}

export interface AlertCard {
  id: string;
  title: string;
  type: 'bucketed' | 'single';
  style: 'positive' | 'negative' | 'neutral' | 'total';
  count?: number;
  filters?: AlertFilter;
  buckets?: AlertBucket[];
}

interface AlertCardProps {
  card: AlertCard;
  isActive: boolean;
  hasActiveBucket: boolean;
  onCardClick: (card: AlertCard) => void;
  onBucketClick: (bucket: AlertBucket) => void;
  isFilterActive: (filters: AlertFilter, activeFilters: AlertFilter) => boolean;
  activeAlertFilters: AlertFilter;
}

export function AlertCardComponent({
  card,
  isActive,
  hasActiveBucket,
  onCardClick,
  onBucketClick,
  isFilterActive,
  activeAlertFilters,
}: AlertCardProps) {
  const count = card.count !== undefined ? card.count : (card.buckets ? card.buckets.reduce((sum, b) => sum + b.count, 0) : 0);
  const isMainCountActive = card.type === 'single' && isActive;

  const getCountColor = () => {
    if (card.type === 'single' && isActive) {
      return 'text-blue-600';
    }
    
    switch (card.style) {
      case 'positive':
        return 'text-green-600';
      case 'negative':
        return 'text-rose-600';
      case 'total':
        return 'text-blue-600';
      case 'neutral':
      default:
        return 'text-slate-600';
    }
  };

  const MainHeaderContent = (
    <div className="flex flex-col space-y-0.5 w-full text-center">
      <CardDescription 
        className={cn(
          "text-lg font-medium leading-tight transition-colors",
          getCountColor(),
          card.type === 'single' && 'group-hover:opacity-80'
        )}
      >
        {count}
      </CardDescription>
      <CardTitle 
        className={cn(
          "text-xs font-medium text-gray-500 tracking-wide leading-tight transition-colors",
          card.type === 'single' && 'group-hover:text-gray-700'
        )}
      >
        {card.title}
      </CardTitle>
    </div>
  );

  return (
    <div 
      className={cn(
        'flex-1 min-w-0',
        card.type === 'single' && 'group cursor-pointer'
      )}
      onClick={card.type === 'single' ? () => onCardClick(card) : undefined}
    >
      <Card
        variant={card.style}
        className={cn(
          "transition-all duration-200 ease-in-out h-full overflow-hidden",
          card.type === 'single' && 'cursor-pointer hover:scale-[1.02]'
        )}
      >
        <div 
          className={cn(
            "p-2.5 relative z-10",
            card.type === 'single' && 'h-full flex items-center justify-center'
          )}
        >
          <CardHeader 
            className={cn(
              "flex flex-col p-0 items-center",
              card.type === 'bucketed' && 'justify-center h-full' 
            )}
          >
            {MainHeaderContent}
          </CardHeader>
        </div>

        {card.type === 'bucketed' && card.buckets && card.buckets.length > 0 && (
          <div 
            className="bg-white/40 backdrop-blur-sm border-t border-white/20 px-2.5 py-1.5 flex flex-row items-center gap-x-3 overflow-x-auto justify-center relative z-10"
          >
            {card.buckets.map((bucket, index) => {
              const isBucketActive = isFilterActive(bucket.filters, activeAlertFilters);
              
              const getBucketCountColor = () => {
                switch (card.style) {
                  case 'positive':
                    return isBucketActive 
                      ? 'text-green-700 group-hover:text-green-800' 
                      : 'text-green-600 group-hover:text-green-700';
                  case 'negative':
                    return isBucketActive 
                      ? 'text-rose-700 group-hover:text-rose-800' 
                      : 'text-rose-600 group-hover:text-rose-700';
                  case 'total':
                    return isBucketActive 
                      ? 'text-blue-700 group-hover:text-blue-800' 
                      : 'text-blue-600 group-hover:text-blue-700';
                  case 'neutral':
                  default:
                    return isBucketActive 
                      ? 'text-slate-700 group-hover:text-slate-800' 
                      : 'text-slate-600 group-hover:text-slate-700';
                }
              };

              return (
                <React.Fragment key={bucket.label}>
                          <div
                            className={cn(
                              "flex items-baseline space-x-1 flex-shrink-0 cursor-pointer transition-all duration-150 group px-2 py-1 rounded-md",
                              isBucketActive && (() => {
                                switch (card.style) {
                                  case 'positive':
                                    return "bg-green-100/80 shadow-sm";
                                  case 'negative':
                                    return "bg-rose-100/80 shadow-sm";
                                  case 'total':
                                    return "bg-blue-100/80 shadow-sm";
                                  case 'neutral':
                                  default:
                                    return "bg-slate-100/80 shadow-sm";
                                }
                              })()
                            )}
                            onClick={(e) => {
                              e.stopPropagation();
                              onBucketClick(bucket);
                            }}
                          >
                            <span className={cn(
                              "text-sm font-medium transition-colors",
                              getBucketCountColor()
                            )}>
                              {bucket.count}
                            </span>
                            <span className={cn(
                              "text-xs font-normal whitespace-nowrap transition-colors",
                              isBucketActive ? (() => {
                                switch (card.style) {
                                  case 'positive':
                                    return "text-green-700";
                                  case 'negative':
                                    return "text-rose-700";
                                  case 'total':
                                    return "text-blue-700";
                                  case 'neutral':
                                  default:
                                    return "text-slate-700";
                                }
                              })() : "text-gray-600 group-hover:text-gray-800"
                            )}>
                              {bucket.label}
                            </span>
                          </div>
                  {index < card.buckets!.length - 1 && (
                    <div className="h-3 w-px bg-white/30 flex-shrink-0" />
                  )}
                </React.Fragment>
              );
            })}
          </div>
        )}
      </Card>
    </div>
  );
}
